<script setup lang="ts">
import type { SelectTriggerProps } from "./select.types";
import { useSharedState } from "composables/useSharedState";
import { useIcons } from "composables/useIcons";
import { useSelect } from "./useSelect";
import { useSelectTrigger } from "./useSelect.Trigger";
import InputTag from "components/Input/InputTag.vue";

// Note: "Trigger" naming follows headless UI patterns (triggers dropdown).
// Alternative consideration: "Header" - but "Trigger" is more semantically accurate.
defineOptions({ name: "SelectTrigger" });

const props = defineProps<SelectTriggerProps>();

const state = useSharedState(props);

const select = useSelect();

const { isLeading, isTrailing, leadingIconName, trailingIconName } = useIcons(state);

const {
  isMultiple,
  tags,
  getTagDisplayValue,
  removeTag,
  displayText,
  isPlaceholder,
  classes,
  tagBindings,
  reorderContainerBindings,
  handleClick,
  handleKeydown,
  tabindex,
} = useSelectTrigger({ props, state });
</script>

<template>
  <e-primitive
    as="div"
    exportparts="base,leading-icon,trailing-icon,select-indicator,select-icon-base"
    :inner-class="classes.base"
    class="e-select-trigger"
    role="combobox"
    :tabindex="tabindex"
    :aria-expanded="select.isOpen.value"
    aria-haspopup="true"
    :aria-disabled="select.selectProps.value?.disabled || undefined"
    :data-multiple="isMultiple || undefined"
    v-bind="reorderContainerBindings()"
    @click="handleClick"
    @keydown="handleKeydown"
  >
    <span
      v-show="isLeading"
      part="leading"
      :class="classes.icon"
      class="e-select-trigger-icon e-select-trigger-leading"
    >
      <slot name="leading">
        <e-icon v-if="leadingIconName" exportparts="base:leading-icon" :name="leadingIconName" size="sm" />
      </slot>
    </span>

    <input-tag
      v-for="(tag, index) in tags"
      :key="String(tag)"
      v-bind="tagBindings(index)"
      part="tag"
      class="e-select-tag"
      :value="tag"
      :display-value="getTagDisplayValue(tag)"
      :color="select.finalColor.value"
      :size="select.finalSize.value"
      :disabled="select.selectProps.value?.disabled"
      @remove="removeTag"
    />

    <span
      v-show="!isMultiple || tags.length === 0"
      part="content"
      :class="[classes.content, isPlaceholder && classes.placeholder]"
      class="e-select-trigger-content"
    >
      <slot>{{ displayText }}</slot>
    </span>

    <span part="trailing" :class="classes.icon" class="e-select-trigger-icon e-select-trigger-trailing">
      <slot name="trailing">
        <e-icon
          v-if="isTrailing && trailingIconName"
          exportparts="base:trailing-icon"
          :name="trailingIconName"
          size="sm"
        />
        <!-- Three-level exportparts chain (intentional, verified):
             e-icon(base) → SelectIndicator(icon-base) → SelectTrigger(select-icon-base)
             "icon-base" is the name SelectIndicator publishes; we re-export it here as the consumer-facing alias. -->
        <e-select-indicator
          v-else
          exportparts="indicator:select-indicator,icon-base:select-icon-base"
        ></e-select-indicator>
      </slot>
    </span>
  </e-primitive>
</template>

<style>
:host {
  display: contents;
}
</style>
